import { useEffect, useMemo, useState } from "react";
import { patientLabel, serviceLabel, type FeeCode, type Page } from "@pagelink/shared";
import { api, ApiError } from "./api.ts";
import { Banner, Btn, Icon, Note, divColor } from "./ui.tsx";

type Item = { page: Page; completedAt: string; outcome: string | null };
type Show = "all" | "unbilled" | "billed";

/* ---------------------------------------------------------------------------
 * Billing. The pages this physician took and completed, by month, each with
 * the fee code it is billed under. Codes are set here or when marking a page
 * done, and the month exports as a CSV for submission.
 * ------------------------------------------------------------------------ */
export function Billing() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState<Item[] | null>(null);
  const [codes, setCodes] = useState<FeeCode[]>([]);
  const [show, setShow] = useState<Show>("all");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { api.picklists().then((r) => setCodes(r.feeCodes)).catch(() => {}); }, []);
  useEffect(() => {
    let live = true;
    setItems(null);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    api.billing(month, end).then((r) => live && setItems(r.items)).catch(() => live && setError("Could not load billing."));
    return () => { live = false; };
  }, [month]);

  const label = useMemo(() => new Map(codes.map((c) => [c.code, c.label])), [codes]);
  const billed = (items ?? []).filter((i) => i.page.feeCode).length;
  const shown = (items ?? []).filter((i) => show === "all" || (show === "billed") === !!i.page.feeCode);
  const step = (n: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const thisMonth = (() => { const d = new Date(); return month.getFullYear() === d.getFullYear() && month.getMonth() === d.getMonth(); })();
  const placeholder = codes.some((c) => /to confirm/i.test(c.label));

  const setCode = async (it: Item, code: string) => {
    setBusy(it.page.id);
    setError(null);
    try {
      const { page } = await api.setFeeCode(it.page.id, code || null);
      setItems((xs) => (xs ?? []).map((x) => (x.page.id === page.id ? { ...x, page } : x)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save the fee code.");
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Completed", "Page", "From", "Service", "Consult type", "Outcome", "Patient", "Fee code", "Fee description"],
      ...(items ?? []).map((i) => [
        new Date(i.completedAt).toLocaleString("en-CA", { hour12: false }),
        i.page.id, i.page.fromName, serviceLabel(i.page.service, i.page.subService, i.page.serviceShort),
        i.page.consultType, i.outcome ?? "", patientLabel(i.page) ?? "", i.page.feeCode ?? "", label.get(i.page.feeCode ?? "") ?? "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `page-com-billing-${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <h1 className="screen-title">Billing</h1>
      <p className="screen-sub">The pages you took and completed, with the fee code each is billed under.</p>

      <div className="cal-nav">
        <button className="icon-btn" aria-label="Previous month" onClick={() => step(-1)}><Icon name="back" size={17} sw={2} /></button>
        <div className="cal-title">{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
        <button className="icon-btn" aria-label="Next month" disabled={thisMonth} onClick={() => step(1)}>
          <Icon name="back" size={17} sw={2} style={{ transform: "scaleX(-1)" }} />
        </button>
      </div>

      {items && (
        <div className="bill-summary">
          <div><b>{items.length}</b><span>completed</span></div>
          <div><b>{billed}</b><span>billed</span></div>
          <div className={items.length - billed > 0 ? "todo" : ""}><b>{items.length - billed}</b><span>not yet</span></div>
        </div>
      )}

      <div className="filterbar">
        <select aria-label="Show" value={show} onChange={(e) => setShow(e.target.value as Show)} style={{ maxWidth: 200 }}>
          <option value="all">All completed pages</option>
          <option value="unbilled">Not billed yet</option>
          <option value="billed">Billed</option>
        </select>
        <span className="count" />
        <Btn sm soft disabled={!items || items.length === 0} onClick={exportCsv}><Icon name="archive" size={13} sw={2} />Export CSV</Btn>
      </div>

      {error && <Banner kind="err">{error}</Banner>}
      {placeholder && <Note warn>The fee codes are placeholders until VGH confirms its list. Do not submit them.</Note>}
      {items && shown.length === 0 && <Note>{items.length === 0 ? "No pages completed this month." : "Nothing here."}</Note>}

      {shown.map((i) => (
        <div key={i.page.id} className="bill-row" style={{ ["--div" as string]: divColor(i.page.service) }}>
          <div className="top">
            <span className="svc">{serviceLabel(i.page.service, i.page.subService, i.page.serviceShort)}</span>
            <span className="when">{new Date(i.completedAt).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div className="mid">
            From {i.page.fromName} · {i.page.consultType}{i.outcome ? ` · ${i.outcome}` : ""}
            {patientLabel(i.page) ? <> · <span className="mono">{patientLabel(i.page)}</span></> : null}
          </div>
          <select aria-label="Fee code" value={i.page.feeCode ?? ""} disabled={busy === i.page.id}
            className={i.page.feeCode ? "" : "unset"} onChange={(e) => void setCode(i, e.target.value)}>
            <option value="">Choose a fee code…</option>
            {codes.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.label}</option>)}
          </select>
        </div>
      ))}
    </>
  );
}
